// ============================================================================
// BESPOKIAN Health - Careers Page
// Job listings with luxury styling
// ============================================================================

import { Navigation } from '@/components/layout/Navigation';
import { Footer } from '@/components/layout/Footer';
import { Briefcase, Heart, Users, TrendingUp, MapPin, ArrowRight } from 'lucide-react';

const openPositions = [
  { title: 'Licensed Pharmacist', location: 'Altamonte Springs, FL (Hybrid)', type: 'Full-time', department: 'Pharmacy' },
  { title: 'Pharmacy Technician', location: 'Altamonte Springs, FL', type: 'Full-time', department: 'Pharmacy' },
  { title: 'Senior Software Engineer', location: 'Remote', type: 'Full-time', department: 'Engineering' },
  { title: 'Customer Support Specialist', location: 'Remote', type: 'Full-time', department: 'Support' },
  { title: 'Marketing Manager', location: 'Altamonte Springs, FL (Hybrid)', type: 'Full-time', department: 'Marketing' },
];

const benefits = [
  { icon: Heart, title: 'Health & Wellness', desc: 'Comprehensive medical, dental, and vision coverage for you and your family' },
  { icon: TrendingUp, title: 'Growth & Equity', desc: '401(k) with 4% match plus equity participation' },
  { icon: Users, title: 'Work-Life Balance', desc: 'Flexible schedules, remote options, and unlimited PTO' },
  { icon: Briefcase, title: 'Development', desc: '$2,000 annual learning budget and mentorship programs' },
];

export function CareersPage() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navigation />

      <main className="flex-1 pt-24">
        {/* Hero */}
        <section className="bg-gradient-to-br from-navy-50 via-white to-gold-50/30 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />

            <p className="text-gold-600 font-medium tracking-wide uppercase text-sm mb-4">
              Careers
            </p>

            <h1 className="font-display text-4xl md:text-5xl text-navy-800 tracking-tight leading-tight mb-6 italic">
              Join Our Team
            </h1>

            <p className="text-xl text-navy-600 max-w-2xl mx-auto">
              Help us make prescription medications affordable and accessible for everyone. 
              We're building something meaningful.
            </p>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />
              <h2 className="font-display text-3xl text-navy-800 tracking-tight italic mb-4">Why BESPOKIAN?</h2>
              <p className="text-navy-600">We take care of our team so they can take care of our patients.</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit) => (
                <div key={benefit.title} className="bg-navy-50 rounded-xl p-6 border border-navy-100 hover:shadow-card transition-shadow">
                  <div className="w-12 h-12 bg-gold-100 rounded-xl flex items-center justify-center mb-4">
                    <benefit.icon className="h-6 w-6 text-gold-600" />
                  </div>
                  <h3 className="font-semibold text-navy-800 mb-2">{benefit.title}</h3>
                  <p className="text-navy-600 text-sm">{benefit.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Open Positions */}
        <section className="py-16 bg-navy-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />
              <h2 className="font-display text-3xl text-navy-800 tracking-tight italic mb-4">Open Positions</h2>
              <p className="text-navy-600">Find your next opportunity with us.</p>
            </div>

            <div className="space-y-4">
              {openPositions.map((job) => (
                <div
                  key={job.title}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-5 bg-white rounded-xl border border-navy-100 hover:border-gold-300 hover:shadow-card transition-all"
                >
                  <div className="mb-3 sm:mb-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-navy-800">{job.title}</h3>
                      <span className="px-2 py-0.5 bg-sage-100 text-sage-700 text-xs font-medium rounded-full">
                        {job.department}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-navy-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {job.location}
                      </span>
                      <span>{job.type}</span>
                    </div>
                  </div>
                  <a
                    href={`mailto:careers@bespokianhealth.com?subject=Application: ${job.title}`}
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-navy-800 bg-gold-500 hover:bg-gold-400 rounded-lg transition-colors"
                  >
                    Apply <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
              ))}
            </div>

            <div className="mt-10 text-center bg-white rounded-xl p-8 border border-navy-100">
              <h3 className="font-display text-xl text-navy-800 tracking-tight mb-2">Don't See the Right Role?</h3>
              <p className="text-navy-600 mb-4">
                We're always looking for talented people. Send us your resume and we'll keep you in mind.
              </p>
              <a
                href="mailto:careers@bespokianhealth.com"
                className="text-gold-600 hover:text-gold-700 font-medium inline-flex items-center gap-1"
              >
                careers@bespokianhealth.com <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}



